import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { useSearchParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import SummaryTab from './tabs/SummaryTab';
import RevenueTab from './tabs/RevenueTab';
import WorkOrdersTab from './tabs/WorkOrdersTab';
import CustomersTab from './tabs/CustomersTab';
import VehiclesTab from './tabs/VehiclesTab';
import EmployeesTab from './tabs/EmployeesTab';
import ProfitabilityTab from './tabs/ProfitabilityTab';
import AccountingTab from './tabs/AccountingTab';
import InventoryTab from './tabs/InventoryTab';
import PurchasesTab from './tabs/PurchasesTab';
import { useUserStore } from '../../store';
import { can } from '../../utils/access';

// Helper to convert "yyyy-MM-dd" string to Date object
const dateStringToDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00');
};

// Helper to convert Date object to "yyyy-MM-dd" string
const dateToDateString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// `control` picks the header period control, `hasSubTabs` says whether ?subtab=
// survives a tab switch, and `isVisible` hides a tab an organization cannot use.
const TABS = [
  { id: 'summary', labelKey: 'reports.tabs.summary', control: 'period' },
  { id: 'revenue', labelKey: 'reports.tabs.revenue', control: 'period', hasSubTabs: true },
  { id: 'workOrders', labelKey: 'reports.tabs.workOrders', control: 'period' },
  { id: 'customers', labelKey: 'reports.tabs.customers', control: 'period' },
  { id: 'vehicles', labelKey: 'reports.tabs.vehicles', control: 'period' },
  { id: 'employees', labelKey: 'reports.tabs.employees', control: 'period', hasSubTabs: true },
  { id: 'profitability', labelKey: 'reports.tabs.profitability', control: 'period', hasSubTabs: true },
  { id: 'accounting', labelKey: 'reports.tabs.accounting', control: 'yearMonth', hasSubTabs: true },
  {
    id: 'inventory',
    labelKey: 'reports.tabs.inventory',
    control: 'yearMonth',
    hasSubTabs: true,
    // Stock value only means something with tracking on, and it is a purchase-cost
    // figure, so it follows the same key that gates costs elsewhere.
    isVisible: (org) =>
      Boolean(org?.inventory_tracking_enabled) && can(org, 'costs.view_purchase_prices'),
  },
  {
    id: 'purchases',
    labelKey: 'reports.tabs.purchases',
    control: 'period',
    hasSubTabs: true,
    // Ostoreskontra: the combined availability flag plus the purchase invoice view key.
    isVisible: (org) => org?.purchase_ledger_available === true && can(org, 'purchase_invoices.view') && can(org, 'costs.view_purchase_prices'),
  },
];

const TAB_IDS = new Set(TABS.map((tab) => tab.id));
const TABS_BY_ID = Object.fromEntries(TABS.map((tab) => [tab.id, tab]));

const PERIODS = [
  { value: 'day', labelKey: 'reports.periods.today' },
  { value: 'week', labelKey: 'reports.periods.thisWeek' },
  { value: 'last_week', labelKey: 'reports.periods.lastWeek' },
  { value: 'month', labelKey: 'reports.periods.thisMonth' },
  { value: 'last_month', labelKey: 'reports.periods.lastMonth' },
  { value: 'year', labelKey: 'reports.periods.thisYear' },
  { value: 'last_year', labelKey: 'reports.periods.lastYear' },
  { value: 'custom', labelKey: 'reports.periods.custom' },
];

function ReportsPage({ t }) {
  const { currentOrganization } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();
  // Keyed by tab id, not by index: a tab that appears only for some organizations
  // would otherwise shift every index behind it once permissions arrive.
  const [activeTabId, setActiveTabId] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam && TAB_IDS.has(tabParam) ? tabParam : TABS[0].id;
  });
  const [period, setPeriod] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // The accounting and inventory tabs pick a calendar month instead of a period.
  const today = new Date();
  const [reportYear, setReportYear] = useState(today.getFullYear());
  const [reportMonth, setReportMonth] = useState(today.getMonth() + 1);

  // Sync tab from URL changes (e.g. browser back/forward). Validated against every
  // tab rather than the visible ones, so a deep link that lands before the
  // organization has loaded is not thrown away.
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && TAB_IDS.has(tabParam) && tabParam !== activeTabId) {
      setActiveTabId(tabParam);
    }
  }, [searchParams]);

  const visibleTabs = useMemo(
    () => TABS.filter((tab) => !tab.isVisible || tab.isVisible(currentOrganization)),
    [currentOrganization]
  );

  const handleTabChange = (event, newTabId) => {
    setActiveTabId(newTabId);
    const newParams = { tab: newTabId };
    // Preserve subtab when staying on tabs that have sub-tabs
    const subtab = searchParams.get('subtab');
    if (TABS_BY_ID[newTabId]?.hasSubTabs && subtab) {
      newParams.subtab = subtab;
    }
    setSearchParams(newParams, { replace: true });
  };

  // Render-only fallback while the organization is still loading, or when the
  // selected tab turns out not to be available. activeTabId is left alone so the
  // tab snaps back as soon as permissions arrive.
  const currentTab = visibleTabs.some((tab) => tab.id === activeTabId)
    ? activeTabId
    : visibleTabs[0].id;
  const usesYearMonth = TABS_BY_ID[currentTab].control === 'yearMonth';

  // Build date params for API calls
  const getDateParams = () => {
    if (period === 'custom' && customStartDate && customEndDate) {
      return { start_date: customStartDate, end_date: customEndDate };
    }
    return { period };
  };

  const renderTabContent = () => {
    const dateParams = getDateParams();

    switch (currentTab) {
      case 'summary':
        return <SummaryTab dateParams={dateParams} />;
      case 'revenue':
        return <RevenueTab dateParams={dateParams} />;
      case 'workOrders':
        return <WorkOrdersTab dateParams={dateParams} />;
      case 'customers':
        return <CustomersTab dateParams={dateParams} />;
      case 'vehicles':
        return <VehiclesTab dateParams={dateParams} />;
      case 'employees':
        return <EmployeesTab dateParams={dateParams} />;
      case 'profitability':
        return <ProfitabilityTab dateParams={dateParams} />;
      case 'accounting':
        return <AccountingTab year={reportYear} month={reportMonth} />;
      case 'inventory':
        return <InventoryTab year={reportYear} month={reportMonth} />;
      case 'purchases':
        return <PurchasesTab dateParams={dateParams} />;
      default:
        return null;
    }
  };

  // Generate year options (last 5 years)
  const yearOptions = [];
  for (let y = today.getFullYear(); y >= today.getFullYear() - 4; y--) {
    yearOptions.push(y);
  }

  // Month options
  const monthOptions = [
    { value: 1, labelKey: 'reports.months.january' },
    { value: 2, labelKey: 'reports.months.february' },
    { value: 3, labelKey: 'reports.months.march' },
    { value: 4, labelKey: 'reports.months.april' },
    { value: 5, labelKey: 'reports.months.may' },
    { value: 6, labelKey: 'reports.months.june' },
    { value: 7, labelKey: 'reports.months.july' },
    { value: 8, labelKey: 'reports.months.august' },
    { value: 9, labelKey: 'reports.months.september' },
    { value: 10, labelKey: 'reports.months.october' },
    { value: 11, labelKey: 'reports.months.november' },
    { value: 12, labelKey: 'reports.months.december' },
  ];

  return (
    <Box data-testid="reports-page">
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography
          component="h4"
          variant="h4"
          data-testid="reports-heading"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('reports.title')}
        </Typography>

        {/* Date/Period Controls */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {usesYearMonth ? (
            <>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>{t('reports.year')}</InputLabel>
                <Select
                  value={reportYear}
                  label={t('reports.year')}
                  onChange={(e) => setReportYear(e.target.value)}
                  data-testid="reports-year-select"
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('reports.month')}</InputLabel>
                <Select
                  value={reportMonth}
                  label={t('reports.month')}
                  onChange={(e) => setReportMonth(e.target.value)}
                  data-testid="reports-month-select"
                >
                  {monthOptions.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{t(m.labelKey)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : (
            <>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('reports.period')}</InputLabel>
                <Select
                  value={period}
                  label={t('reports.period')}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  {PERIODS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{t(p.labelKey)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {period === 'custom' && (
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                  <DatePicker
                    label={t('reports.startDate')}
                    value={dateStringToDate(customStartDate)}
                    onChange={(newValue) => setCustomStartDate(dateToDateString(newValue))}
                    format="dd.MM.yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: 160 },
                      },
                    }}
                  />
                  <DatePicker
                    label={t('reports.endDate')}
                    value={dateStringToDate(customEndDate)}
                    onChange={(newValue) => setCustomEndDate(dateToDateString(newValue))}
                    format="dd.MM.yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: 160 },
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontWeight: 500,
              textTransform: 'none',
              minWidth: 'auto',
              px: 3,
            },
          }}
        >
          {visibleTabs.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={t(tab.labelKey)}
              data-testid={`reports-tab-${tab.id}`}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {renderTabContent()}
    </Box>
  );
}

export default withTranslation()(ReportsPage);
